import { useMemo, useState } from 'react';
import type { CatalogCountry } from '@/api/types';
import { searchCountries } from '@/lib/countries';
import { cn } from '@/lib/cn';
import { Icon } from './Icon';
import { Sheet } from './Modal';

function SearchBox({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <label className="relative block">
      <span className="sr-only">Search countries</span>
      <svg viewBox="0 0 24 24" className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search countries"
        autoComplete="off"
        data-testid={testId}
        className="h-12 w-full rounded-[16px] border border-line bg-surface pl-10 pr-4 text-[16px] text-fg placeholder:text-muted focus:outline-none focus-visible:border-fg"
        data-autofocus
      />
    </label>
  );
}

/** Single-select, searchable country picker (button + sheet). */
export function CountryPicker({
  value,
  onChange,
  countries,
  testId,
  label = 'Country',
  disabled,
}: {
  value: string | null;
  onChange: (code: string) => void;
  countries: CatalogCountry[];
  testId?: string;
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const list = useMemo(() => searchCountries(countries, q), [countries, q]);
  const current = countries.find((c) => c.code === value);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid={testId}
        data-value={value ?? ''}
        aria-haspopup="dialog"
        aria-label={`${label}: ${current?.name ?? 'not set'}`}
        className="flex h-14 w-full items-center gap-3 rounded-[16px] border border-line bg-surface px-4 text-left transition-colors hover:border-fg/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-50"
      >
        <span className="text-2xl leading-none" aria-hidden>
          {current?.flag ?? '🌍'}
        </span>
        <span className={cn('flex-1 truncate text-[16px] font-semibold', !current && 'text-muted')}>{current?.name ?? 'Choose a country'}</span>
        <Icon name="chevron-down" size={18} className="text-muted" />
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={label} testId="country-sheet" maxHeight="88vh">
        <SearchBox value={q} onChange={setQ} testId="country-search" />
        <ul className="mt-3 max-h-[52vh] overflow-y-auto" role="listbox" aria-label="Countries">
          {list.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                role="option"
                aria-selected={c.code === value}
                data-testid={`country-option-${c.code}`}
                onClick={() => {
                  onChange(c.code);
                  setOpen(false);
                  setQ('');
                }}
                className={cn('flex h-12 w-full items-center gap-3 rounded-control px-3 text-left text-[15px] hover:bg-raised', c.code === value && 'bg-raised font-semibold')}
              >
                <span className="text-xl leading-none" aria-hidden>
                  {c.flag}
                </span>
                <span className="flex-1 truncate">{c.name}</span>
                {c.code === value && <Icon name="check" size={18} className="text-go" />}
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="px-3 py-6 text-center text-sm text-muted">No country matches “{q}”.</li>}
        </ul>
      </Sheet>
    </>
  );
}

/** Inline multi-select with search (used by match filters). */
export function CountryMultiPicker({
  value,
  onChange,
  countries,
  max,
  disabled,
}: {
  value: string[];
  onChange: (codes: string[]) => void;
  countries: CatalogCountry[];
  max: number;
  disabled?: boolean;
}) {
  const [q, setQ] = useState('');
  const list = useMemo(() => searchCountries(countries, q), [countries, q]);
  const full = value.length >= max;
  const toggle = (code: string) => onChange(value.includes(code) ? value.filter((c) => c !== code) : full ? value : [...value, code]);
  const selected = value.map((code) => countries.find((c) => c.code === code) ?? { code, name: code, flag: '' });
  return (
    <div>
      {selected.length > 0 && (
        <ul className="mb-3 flex flex-wrap gap-1.5" aria-label="Selected countries">
          {selected.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle(c.code)}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-fg pl-2.5 pr-2 text-sm font-semibold text-ground"
                aria-label={`Remove ${c.name}`}
              >
                <span aria-hidden>{c.flag}</span> {c.name}
                <Icon name="x" size={14} strokeWidth={2.6} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <SearchBox value={q} onChange={setQ} testId="filter-country-search" />
      <ul className="mt-2 max-h-[220px] overflow-y-auto rounded-[16px] bg-surface" aria-label="Countries">
        {list.map((c) => {
          const on = value.includes(c.code);
          return (
            <li key={c.code}>
              <button
                type="button"
                aria-pressed={on}
                disabled={disabled || (!on && full)}
                data-testid={`filter-country-${c.code}`}
                onClick={() => toggle(c.code)}
                className={cn('flex h-11 w-full items-center gap-3 px-3 text-left text-[15px] transition-colors hover:bg-raised disabled:cursor-not-allowed disabled:opacity-40', on && 'bg-raised font-semibold')}
              >
                <span className="text-lg leading-none" aria-hidden>
                  {c.flag}
                </span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className={cn('flex h-5 w-5 items-center justify-center rounded-md border', on ? 'border-green bg-green text-night' : 'border-line')} aria-hidden>
                  {on && <Icon name="check" size={14} strokeWidth={3} />}
                </span>
              </button>
            </li>
          );
        })}
        {list.length === 0 && <li className="px-3 py-5 text-center text-sm text-muted">No country matches “{q}”.</li>}
      </ul>
      <p className="mt-2 text-xs text-muted">{full ? `That’s the max — ${max} countries.` : `Up to ${max}. Leave empty for anywhere.`}</p>
    </div>
  );
}
